import _ from "lodash";
import update from "immutability-helper";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import client from "lib/apolloClient/client";
import {
  createAdminAttendanceExcusalMutation,
  createAttendanceExcusalMutation,
  updateAttendanceExcusalMutation,
  deleteAttendanceExcusalMutation,
} from "./AttendanceSummaryMutation";
import { getStudentAttendanceExcusalQuery } from "./AttendanceSummaryQuery";
import {
  getOrganizationAttendanceExcusalQuery,
  getOrganizationAttendanceExcusalCountQuery,
  getStudentTabsAllAttendanceDetailsQuery,
  getStudentTabsAttendanceDetailsQuery,
} from "AttendanceDashboard/modules/AttendanceDashboardQueries";
import { checkIsStudentListPaginationEnhancementEnabled } from "AttendanceDashboard/modules/Utils";
import {
  getExcusalCountQuery,
  getStudentAllExcusalQuery,
} from "Homeroom/modules/HomeroomQueries";
import { formatDate } from "Utils";
import { ATTENDANCE_CONSTANTS } from "AttendanceDashboard/constants/stringConstants";
import ACLStore from "lib/aclStore";

export const NAME = "attendanceSummary";
const UPDATE_PRIMARY_FILTER = "UPDATE_PRIMARY_FILTER" + " " + NAME;
const UPDATE_SECONDARY_FILTER = "UPDATE_SECONDARY_FILTER" + " " + NAME;
const UPDATE_TERTIARY_FILTER = "UPDATE_TERTIARY_FILTER" + " " + NAME;
const UPDATE_VIEW_TYPE = "UPDATE_VIEW_TYPE" + " " + NAME;
const MAX_ALLOWABLE_PERIOD_NAME_LENGTH = 20;
import moment from "moment";

export const ATTENDANCE_RECORDS_VIEW_TYPE = Object.freeze({
  ALL_RECORDS: "ALL_RECORDS",
  FILTERED: "FILTERED",
  CALENDAR: "CALENDAR",
});

export const ATTENDANCE_RECORDS_FILTER_TYPE = Object.freeze({
  COURSE: "COURSE",
  CATEGORY: "CATEGORY",
  CODE: "CODE",
});

export const getHeaderData = ({ t, isMultipleDaysExcusalExists }) => {
  return [
    {
      key: "durationTime",
      value: t("common:duration"),
      style: {
        width: isMultipleDaysExcusalExists ? "168px" : "124px",
        zIndex: 90,
      },
      isSticky: true,
    },
    {
      key: "excusal",
      value: t("attendance:excusal"),
      style: {
        width: isMultipleDaysExcusalExists ? "416px" : "460px",
      },
    },
    {
      key: "requestedBy",
      value: t("attendance:requested_by"),
      style: {
        minWidth: "164px",
      },
    },
  ];
};

const generateColumns = ({
  isPeriodByAttendance,
  periods,
  primaryFilterType,
  secondaryFilterType,
  t,
}) => {
  if (
    isPeriodByAttendance &&
    !secondaryFilterType &&
    (!primaryFilterType ||
      primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY)
  ) {
    return [
      {
        id: "day",
        value: t("common:capitalize", {
          text: t("common:day"),
        }),
      },
      ..._.map(periods, (period, index) => ({
        value: `P${index + 1}`,
        id: _.get(period, "id"),
        label: _.get(period, "label"),
        abbreviation: _.get(period, "abbreviation", ""),
      })),
    ];
  } else {
    return [
      {
        id: "day",
        value: t("common:capitalize", {
          text: t("common:day"),
        }),
      },
      ...(isPeriodByAttendance &&
      primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY
        ? [
            {
              id: "class",
              value: t("common:class"),
            },
          ]
        : []),
      ...(isPeriodByAttendance &&
      primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE
        ? [
            {
              id: "period",
              value: t("common:period"),
            },
          ]
        : []),
      {
        id: "attendance",
        value: t("common:attendance"),
      },
      {
        id: "comment",
        value: t("common:comment"),
      },
    ];
  }
};

export const createAdminAttendanceExcusal = ({
  studentName,
  studentIds,
  durationType,
  startDate,
  endDate,
  reason,
  periodIds,
  academicYearStartDate,
  academicYearEndDate,
  attendanceOptionId,
  attachments,
  curriculumProgramId,
  academicYearId,
  sourceId,
  sourceMetaData,
  organizationId,
  attendanceOptionIds,
  studentAttendanceDetailsVariables,
}) => {
  const today = moment();
  const todayFormatted = today.format(ATTENDANCE_CONSTANTS.YYYY_MM_DD);

  const {
    searchText = "",
    selectedCourses,
    allCourses = [],
    sortEntity,
    direction,
  } = sourceMetaData || {};

  const allCourseIds = _.map(allCourses, ({ node }) => node.id);
  const isHomeroom = sourceId === "HOMEROOM";

  return async (dispatch, getState) => {
    try {
      let refetchQueries = [];

      switch (sourceId) {
        case "STUDENT_PROFILE":
        case "ATTENDANCE_SUMMARY":
          refetchQueries = [
            {
              query: getStudentAttendanceExcusalQuery,
              variables: {
                studentId: _.first(studentIds),
                filters: {
                  startDate: academicYearStartDate,
                  endDate: academicYearEndDate,
                },
              },
            },
          ];
          break;
        case "BULK_EXCUSAL":
          refetchQueries = [
            checkIsStudentListPaginationEnhancementEnabled()
              ? {
                  query: getStudentTabsAttendanceDetailsQuery,
                  variables: studentAttendanceDetailsVariables,
                }
              : {
                  query: getStudentTabsAllAttendanceDetailsQuery,
                  variables: studentAttendanceDetailsVariables,
                },
          ];
          break;
        default:
          refetchQueries = [
            {
              query: getOrganizationAttendanceExcusalQuery,
              variables: {
                id: organizationId,
                first: 15,
                orderBy: sortEntity,
                orderByDirection: direction,
                filters: {
                  startDate: academicYearStartDate,
                  endDate: academicYearEndDate,
                  searchText,
                  attendanceExcusalStatus: "PENDING",
                  attendanceOptionIds: [],
                  curriculumProgramIds: curriculumProgramId,
                  courseIds: !isHomeroom ? selectedCourses : undefined,
                  academicYearIds: academicYearId,
                  visibleTo: isHomeroom ? "HOMEROOM" : undefined,
                },
                isOngoingExcusalVisible: false,
              },
            },
            {
              query: isHomeroom
                ? getExcusalCountQuery
                : getOrganizationAttendanceExcusalCountQuery,
              variables: {
                id: organizationId,
                orderBy: !isHomeroom
                  ? ATTENDANCE_CONSTANTS.EXCUSAL_START_DATE
                  : undefined,
                orderByDirection: !isHomeroom
                  ? ATTENDANCE_CONSTANTS.DESC
                  : undefined,
                newExcusalsFilters: {
                  startDate: academicYearStartDate,
                  endDate: academicYearEndDate,
                  attendanceExcusalStatus: ATTENDANCE_CONSTANTS.PENDING,
                  attendanceOptionIds: !isHomeroom
                    ? attendanceOptionIds
                    : undefined,
                  curriculumProgramIds: curriculumProgramId,
                  courseIds: !isHomeroom ? allCourseIds : undefined,
                  academicYearIds: academicYearId,
                  visibleTo: isHomeroom ? "HOMEROOM" : undefined,
                },
                upcomingAndOngoingExcusalsFilters: {
                  startDate: todayFormatted,
                  endDate: academicYearEndDate,
                  attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
                  attendanceOptionIds: !isHomeroom
                    ? attendanceOptionIds
                    : undefined,
                  curriculumProgramIds: curriculumProgramId,
                  courseIds: !isHomeroom ? allCourseIds : undefined,
                  academicYearIds: academicYearId,
                  visibleTo: isHomeroom ? "HOMEROOM" : undefined,
                },
                markedExcusalsFilters: {
                  startDate: academicYearStartDate,
                  endDate: academicYearEndDate,
                  attendanceExcusalStatus: ATTENDANCE_CONSTANTS.MARKED,
                  attendanceOptionIds: !isHomeroom
                    ? attendanceOptionIds
                    : undefined,
                  curriculumProgramIds: curriculumProgramId,
                  courseIds: !isHomeroom ? allCourseIds : undefined,
                  academicYearIds: academicYearId,
                  visibleTo: isHomeroom ? "HOMEROOM" : undefined,
                },
                studentsFilters: isHomeroom
                  ? {
                      academicYearIds: academicYearId,
                      curriculumProgramIds: curriculumProgramId,
                      visibleTo: "HOMEROOM",
                    }
                  : undefined,
              },
            },
          ];
          break;
      }

      await client.mutate({
        mutation: createAdminAttendanceExcusalMutation,
        variables: {
          studentIds,
          durationType,
          startDate,
          endDate,
          reason,
          periodIds,
          attachments,
          attendanceOptionId,
          curriculumProgramId,
          academicYearId,
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });

      const toastMsg =
        sourceId === "STUDENT_PROFILE"
          ? `toastMsgs:excusal_created_for_student_name`
          : `toastMsgs:excusal_created_for_students`;
      const toastLocaleParams =
        sourceId === "STUDENT_PROFILE"
          ? [{ key: "studentName", value: studentName, isPlainText: true }]
          : [
              {
                key: "count",
                value: _.size(studentIds),
                isPlainText: true,
              },
            ];
      dispatch(
        setToastMsg({
          msg: toastMsg,
          locale_params: toastLocaleParams,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      throw e;
    }
  };
};

export const createAttendanceExcusal = ({
  studentId,
  durationType,
  startDate,
  endDate,
  reason,
  periodIds,
  academicYearStartDate,
  academicYearEndDate,
  attachments,
  curriculumProgramId,
}) => {
  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: createAttendanceExcusalMutation,
        variables: {
          studentId,
          durationType,
          startDate,
          endDate,
          reason,
          periodIds,
          attachments,
          curriculumProgramId,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentAttendanceExcusalQuery,
            variables: {
              studentId,
              filters: {
                startDate: academicYearStartDate,
                endDate: academicYearEndDate,
              },
            },
          },
        ],
      });
      dispatch(
        setToastMsg({
          msg: ACLStore.can("Common:Homeroom")
            ? "toastMsgs:excusal_sent_successfully"
            : "toastMsgs:sent_successfully",
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      throw e;
    }
  };
};

export const updateAdminAttendanceExcusal = ({
  isHomeroom,
  isStudent,
  excusalId,
  durationType,
  startDate,
  endDate,
  reason,
  periodIds,
  attendanceOptionId,
  newAttachments,
  removedAttachments,
  curriculumProgramId,
  academicYearId,
  excusalQueryVariables,
  excusalCountsQueryVariables,
}) => {
  return async (dispatch, getState) => {
    try {
      const refetchQueries = isStudent
        ? [
            {
              query: getStudentAllExcusalQuery,
              variables: excusalQueryVariables,
            },
          ]
        : [
            {
              query: getOrganizationAttendanceExcusalQuery,
              variables: excusalQueryVariables,
            },
            {
              query: isHomeroom
                ? getExcusalCountQuery
                : getOrganizationAttendanceExcusalCountQuery,
              variables: excusalCountsQueryVariables,
            },
          ];

      await client.mutate({
        mutation: updateAttendanceExcusalMutation,
        variables: {
          excusalId,
          durationType,
          startDate,
          endDate,
          reason,
          periodIds,
          newAttachments,
          removedAttachments,
          curriculumProgramId,
          academicYearId,
          attendanceOptionId,
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:excusal_updated_successfully",
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      throw e;
    }
  };
};

export const updateAttendanceExcusal = ({
  studentId,
  excusalId,
  durationType,
  startDate,
  endDate,
  reason,
  periodIds,
  academicYearStartDate,
  academicYearEndDate,
  newAttachments,
  removedAttachments,
  curriculumProgramId,
  academicYearId,
}) => {
  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: updateAttendanceExcusalMutation,
        variables: {
          excusalId,
          durationType,
          startDate,
          endDate,
          reason,
          periodIds,
          newAttachments,
          removedAttachments,
          curriculumProgramId,
          academicYearId,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentAttendanceExcusalQuery,
            variables: {
              studentId,
              filters: {
                startDate: academicYearStartDate,
                endDate: academicYearEndDate,
              },
            },
          },
        ],
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:excusal_updated_successfully",
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      throw e;
    }
  };
};

export const deleteAttendanceExcusal = ({ deleteExcusalInput, excusal }) => {
  const firstName = _.get(excusal, "student.firstName", "");
  const { durationType, startDate, endDate } = excusal;

  const formattedStartDate = formatDate({
    date: startDate,
    format: ATTENDANCE_CONSTANTS.DD_MMM,
  });

  const formattedEndDate = formatDate({
    date: endDate,
    format: ATTENDANCE_CONSTANTS.DD_MMM_YYYY,
  });

  const formattedDate =
    durationType === ATTENDANCE_CONSTANTS.MULTIPLE_DAYS
      ? `${formattedStartDate}-${formattedEndDate}`
      : `${formattedEndDate}`;

  return async (dispatch, getState) => {
    try {
      await client.mutate({
        mutation: deleteAttendanceExcusalMutation,
        variables: {
          input: deleteExcusalInput,
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:deleted_student_excusal_request",
          locale_params: [
            {
              key: "name",
              value: firstName,
              isPlainText: true,
            },
            {
              key: "date",
              value: formattedDate,
              isPlainText: true,
            },
          ],
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
      throw e;
    }
  };
};

const getTruncatedPeriodsDetails = ({ currentSelectedPeriods, t }) => {
  const formattedPeriodNames = _.map(currentSelectedPeriods, period =>
    _.get(period, "label", "")
  );
  const firstPeriodName = _.first(formattedPeriodNames);
  const periodsLength = formattedPeriodNames.length;
  if (periodsLength === 1) {
    return firstPeriodName.length <= MAX_ALLOWABLE_PERIOD_NAME_LENGTH
      ? firstPeriodName
      : `${firstPeriodName.substring(0, 16)}...`;
  }
  if (firstPeriodName.length <= 13) {
    return t("common:number_more", {
      number: `${firstPeriodName} +${periodsLength - 1}`,
    });
  }
  return t("common:number_more", {
    number: `${firstPeriodName.substring(0, 10)}... +${periodsLength - 1}`,
  });
};

export const getTruncatedPeriodsDetailsMemoized = _.memoize(
  params => getTruncatedPeriodsDetails(params),
  params => JSON.stringify(params)
);

export const generateColumnsMemoized = _.memoize(
  params => generateColumns(params),
  params => JSON.stringify(params)
);

const createTooltip = ({
  totalCount,
  filteredCount,
  isPeriodByAttendance,
  primaryFilter,
  secondaryFilter,
  tertiaryFilter,
  t,
  isHomeroomPeriodEnabled,
  isHomeroomData,
}) => {
  const { type: primaryFilterType, label: primaryFilterLabel } =
    primaryFilter || {};
  const { type: secondaryFilterType, label: secondaryFilterLabel } =
    secondaryFilter || {};
  const { type: tertiaryFilterType, label: tertiaryFilterLabel } =
    tertiaryFilter || {};
  if (isHomeroomPeriodEnabled) {
    if (isHomeroomData) {
      return t("attendance:homeroom_period_statistics_desc", {
        categoryName: primaryFilterLabel,
      });
    }

    return t("attendance:student_attendance_statistics_desc", {
      categoryName: primaryFilterLabel,
    });
  } else if (isPeriodByAttendance) {
    if (
      primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE &&
      secondaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY
    ) {
      return t("attendance:course_weightage_periods", {
        categoryName: secondaryFilterLabel,
        periodsCount: filteredCount,
        totalCount,
        courseName: primaryFilterLabel,
      });
    } else if (primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY) {
      if (secondaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE) {
        return t("attendance:course_weightage_periods", {
          categoryName: primaryFilterLabel,
          periodsCount: filteredCount,
          totalCount,
          courseName: secondaryFilterLabel,
        });
      }
      return t("attendance:category_weightage_periods", {
        categoryName: primaryFilterLabel,
        periodsCount: filteredCount,
        totalCount,
      });
    }
  } else {
    return t("attendance:category_weightage_days", {
      categoryName: primaryFilterLabel,
      daysCount: filteredCount,
      totalDays: totalCount,
    });
  }
};

export const createTooltipMemoized = _.memoize(
  params => createTooltip(params),
  params => JSON.stringify(params)
);

const createEmptyStateText = ({
  primaryFilter,
  secondaryFilter,
  tertiaryFilter,
  t,
}) => {
  const { type: primaryFilterType, label: primaryFilterLabel } =
    primaryFilter || {};
  const { type: secondaryFilterType, label: secondaryFilterLabel } =
    secondaryFilter || {};
  const { type: tertiaryFilterType, label: tertiaryFilterLabel } =
    tertiaryFilter || {};
  if (
    primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE &&
    secondaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY
  ) {
    return t("attendance:empty_text_category_for_course", {
      categoryName: secondaryFilterLabel,
      courseName: primaryFilterLabel,
    });
  }
  if (
    primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY &&
    secondaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE
  ) {
    return t("attendance:empty_text_category_for_course", {
      categoryName: primaryFilterLabel,
      courseName: secondaryFilterLabel,
    });
  }
  if (
    primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.CATEGORY &&
    !secondaryFilterType
  ) {
    return t("attendance:empty_text_category", {
      categoryName: primaryFilterLabel,
    });
  }
  if (
    primaryFilterType === ATTENDANCE_RECORDS_FILTER_TYPE.COURSE &&
    !secondaryFilterType
  ) {
    return t("attendance:empty_text_course", {
      courseName: primaryFilterLabel,
    });
  }
};

export const createEmptyStateTextMemoized = _.memoize(
  params => createEmptyStateText(params),
  params => JSON.stringify(params)
);

export const setPrimaryFilter = data => {
  return { type: UPDATE_PRIMARY_FILTER, payload: data };
};

export const setSecondaryFilter = data => {
  return { type: UPDATE_SECONDARY_FILTER, payload: data };
};

export const setTertiaryFilter = data => {
  return { type: UPDATE_TERTIARY_FILTER, payload: data };
};

export const setViewType = data => {
  return { type: UPDATE_VIEW_TYPE, payload: data };
};

const REDUCER_HANDLERS = {
  [UPDATE_PRIMARY_FILTER]: (state, action) => {
    return update(state, {
      primaryFilter: { $set: action.payload },
    });
  },
  [UPDATE_SECONDARY_FILTER]: (state, action) => {
    return update(state, {
      secondaryFilter: { $set: action.payload },
    });
  },
  [UPDATE_TERTIARY_FILTER]: (state, action) => {
    return update(state, {
      tertiaryFilter: { $set: action.payload },
    });
  },
  [UPDATE_VIEW_TYPE]: (state, action) => {
    return update(state, {
      viewType: { $set: action.payload },
    });
  },
};

export const initialState = {
  primaryFilter: null,
  secondaryFilter: null,
  tertiaryFilter: null,
  viewType: ATTENDANCE_RECORDS_VIEW_TYPE.ALL_RECORDS,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
